/* eslint-disable import/first */

__webpack_public_path__ = window.__PUBLIC_PATH__ || '/';

import { shouldPolyfill as shouldPolyfillLocale } from '@formatjs/intl-locale/should-polyfill';
import { shouldPolyfill as shouldPolyfillNumberFormat } from '@formatjs/intl-numberformat/should-polyfill';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import debug from 'debug';
import { once } from 'lodash';
import React from 'react';
import { hydrate, render } from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import Loadable from 'react-loadable';
import ReactModal from 'react-modal';
import { BrowserRouter as Router } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { WMTConfig } from '@swf/core/api/config';
import { get } from '@swf/core/api/embedded';
import { subscribe } from '@swf/core/api/subscribe';
import AnalyticsProvider from '@swf/core/providers/AnalyticsProvider';
import AuthProvider from '@swf/core/providers/AuthProvider';
import RootHistoryContextProvider from '@swf/core/providers/RootHistoryContextProvider';
import store, { dispatch, getState, select } from '@swf/core/store';
import CookieNames from '@swf/core/types/Cookies';

import Providers from './Providers';
import Routes from './Routes';

window.__STARS_DEBUG__ = debug;

const rootElem = document.getElementById('root') as HTMLElement;
const state = getState();
const cookies = new Cookies();

const loadIntl = async () => {
  let { locale = 'en' } = window.__INITIAL_STATE__;

  if (shouldPolyfillLocale()) {
    await import('@formatjs/intl-locale/polyfill');
  }

  await import(/* webpackChunkName: "intl" */ '@formatjs/intl-numberformat/polyfill');

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (Intl.NumberFormat.polyfilled) {
    if (locale === 'zh-CN') {
      locale = 'zh-Hans';
    } else if (locale === 'zh-TW') {
      locale = 'zh-Hant';
    } else if (locale === 'pt-BR') {
      locale = 'pt';
    } else if (locale === 'no') {
      locale = 'nb';
    }

    await import(
      /* webpackInclude: /\/(es-419|zh-Hans|zh-Hant|bg|cs|da|de|el|en|es|et|fi|fr|hr|hu|it|ja|ko|lt|lv|nb|pl|pt|ro|ru|sl|sv|uk)\.js$/ */
      `@formatjs/intl-numberformat/locale-data/${locale}`
    );
  }
};

const { sentryDsn, sentrySampleRate, sentryEnvironment } = state.config;

/* eslint-disable no-console */
const startup = once(async () => {
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [new BrowserTracing()],
      environment: sentryEnvironment,
      release: window.version,
      tracesSampleRate: sentrySampleRate
    });

    Sentry.setContext('app', {
      brand: select.site.getBrandName(state),
      site: select.site.getName(state)
    });
  }

  subscribe(WMTConfig().responseChannel).subscribe(() => undefined);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const prefetch: Promise<any>[] = [Loadable.preloadReady()];
  const domain = select.location.getCookieDomain(state);
  cookies.set(CookieNames.LANGUAGE, select.locale.get(state), { domain, path: '/' });

  if (shouldPolyfillNumberFormat()) prefetch.push(loadIntl());
  await Promise.all(prefetch).catch((err) => undefined);
  await dispatch.page.preload();

  ReactModal.setAppElement('#root');
  process.env.ENABLE_PROFILING && console.time && console.time('ReactRendering');

  (rootElem.childNodes.length ? hydrate : render)(
    <Providers store={store}>
      <HelmetProvider>
        <AnalyticsProvider>
          <Router basename={select.location.getBaseURL(state)}>
            <AuthProvider>
              <RootHistoryContextProvider>
                <Routes />
              </RootHistoryContextProvider>
            </AuthProvider>
          </Router>
        </AnalyticsProvider>
      </HelmetProvider>
    </Providers>,
    rootElem,
    async () => {
      process.env.ENABLE_PROFILING && console.timeEnd && console.timeEnd('ReactRendering');
      await dispatch.auth.verifySession();
      dispatch.site.fetch();
      dispatch.system.check();
      dispatch.system.subscribe();

      if (process.env.NODE_ENV === 'development') {
        dispatch.site.fetchAlternateLinks();
        dispatch.site.fetchLocales();
      }
    }
  );

  const jssStyles = document.getElementById('jss-ssr');
  if (jssStyles?.parentElement) jssStyles.parentElement.removeChild(jssStyles);
});
/* eslint-enable */

const { query } = select.location.get(state);
if (select.site.isDesktopEmbedded(state) && !query.active) {
  document.addEventListener('starsresume', startup);
} else if (select.site.isEmbedded(state) && !get('app.ready')) {
  document.addEventListener('starsready', startup);
} else {
  startup();
}
